import moment from 'moment'
import Logger from '../app/common/Logger'

const GATHER_STATS_INTERVAL = 3000
const logger = new Logger('PeerConnection')

export class PeerConnection {
    constructor(iceServers, localStream, onCandidate, onRemoteStream, direction) {
        this.iceServers = iceServers
        this.localStream = localStream
        this.onCandidate = onCandidate
        this.onRemoteStream = onRemoteStream
        this.direction = direction

        this.pc = null
        this.stats = null
        this.connectionTime = null
        this.noStats = false
        this.statsInterval = null

        logger.info(`Creating PeerConnection with ICE servers: ${JSON.stringify(iceServers)}`)

        this.initialize()
    }

    /**
     * Инициализация
     * @private
     */
    initialize() {
        const config = { iceServers: this.iceServers }

        try {
            this.pc = new RTCPeerConnection(config)
            this.setupEventHandlers()
            this.addLocalStream()
            this.startStatsCollection()

            logger.info('PeerConnection initialized successfully')
        } catch (e) {
            logger.error(e, 'Failed to create RTCPeerConnection')
            throw e
        }
    }

    /**
     * Установка обработчиков событий PeerConnection
     * @private
     */
    setupEventHandlers() {
        this.pc.onicecandidate = (event) => {
            if (event.candidate) {
                logger.info('ICE candidate generated')
                this.onCandidate(event.candidate)
            }
        }

        this.pc.oniceconnectionstatechange = (event) => {
            logger.info(`ICE connection state changed: ${this.pc.iceConnectionState}`)

            if (this.pc.iceConnectionState === 'connected') {
                this.connectionTime = moment().unix()
                this.onRemoteStream({ stream: false, connectingState: true })
                this.noStats = false
            } else if (this.pc.iceConnectionState === 'disconnected') {
                this.noStats = true
                logger.info('Connection disconnected, stopping stats collection')
            }
        }

        this.pc.ontrack = (event) => {
            logger.info('Remote track received')
            this.onRemoteStream({ stream: event.streams[0], connectingState: false })
        }

        this.pc.onremovetrack = (event) => {
            logger.info('Remote track removed')
            this.onRemoteStream({ stream: null, connectingState: false })
            this.noStats = true
        }
    }

    /**
     * Длбавляем локальный стрим в PeerConnection
     * @private
     */
    addLocalStream() {
        if (this.localStream) {
            logger.info('Adding local stream to peer connection')

            // Добавляем треки
            this.localStream.getTracks().forEach(track => {
                this.pc.addTrack(track, this.localStream)
            })

            // Устанавливаем направление, если есть
            if (this.direction) {
                this.pc.getTransceivers().forEach(transceiver => {
                    transceiver.direction = this.direction
                })
            }
        }
    }

    /**
     * Устанавливаем интервал сбора статистики
     * @private
     */
    startStatsCollection() {
        this.statsInterval = setInterval(async () => {
            if (!this.pc || this.noStats) return

            try {
                const statsReport = await this.pc.getStats()
                this.stats = this.processStatsReport(statsReport)
            } catch (e) {
                logger.error(e, 'Failed to collect statistics')
            }
        }, GATHER_STATS_INTERVAL)
    }

    /**
     * Обработка статистики вызова
     * @param {RTCStatsReport} report - сырые данные статистики
     * @returns {Object} обработанная статистика
     * @private
     */
    processStatsReport(report) {
        let transport, outboundRtp, inboundRtp, remoteInboundRtp, codec, videoCodec
        let selectedCandidatePairId, localCandidate, remoteCandidate
        const candidatePairs = {}
        const localCandidates = {}
        const remoteCandidates = {}

        // Обрабатываем по типу
        report.forEach(stat => {
            switch (stat.type) {
                case 'transport':
                    transport = stat
                    break
                case 'outbound-rtp':
                    if (stat.kind === 'audio') {
                        outboundRtp = stat
                        codec = report.get(stat.codecId)
                    } else if (stat.kind === 'video') {
                        videoCodec = report.get(stat.codecId)
                    }
                    break
                case 'inbound-rtp':
                    if (stat.kind === 'audio') inboundRtp = stat
                    break
                case 'remote-inbound-rtp':
                    if (stat.kind === 'audio') remoteInboundRtp = stat
                    break
                case 'candidate-pair':
                    if (stat.selected) selectedCandidatePairId = stat.id
                    candidatePairs[stat.id] = stat
                    break
                case 'local-candidate':
                    localCandidates[stat.id] = stat
                    break
                case 'remote-candidate':
                    remoteCandidates[stat.id] = stat
                    break
            }
        })

        // Ищем ид. выбранной пары кандидатов
        if (!selectedCandidatePairId) {
            selectedCandidatePairId = transport?.selectedCandidatePairId
        }

        if (selectedCandidatePairId) {
            const candidatePair = candidatePairs[selectedCandidatePairId]
            const localCandidateId = candidatePair?.localCandidateId
            localCandidate = localCandidateId ? localCandidates[localCandidateId] : null
            const remoteCandidateId = candidatePair?.remoteCandidateId
            remoteCandidate = remoteCandidateId ? remoteCandidates[remoteCandidateId] : null
        }

        // Считаем MOS
        const packetsLost = inboundRtp?.packetsLost || 0
        const delayMs = remoteInboundRtp?.roundTripTime
            ? Math.round(remoteInboundRtp.roundTripTime * 1000)
            : 0
        const jitterBufferMs = inboundRtp?.jitter
            ? Math.round(inboundRtp.jitter * 1000)
            : 0
        const MOS = this.calculateMOS(delayMs, jitterBufferMs, packetsLost)

        return {
            bytesReceived: transport?.bytesReceived?.toString() || '0',
            bytesSend: transport?.bytesSent?.toString() || '0',
            codec: codec?.mimeType?.split('/')[1] || '',
            videoCodec: videoCodec?.mimeType?.split('/')[1] || '',
            delayMs: delayMs.toString(),
            jitterBufferMs: jitterBufferMs.toString(),
            packetsLost: packetsLost.toString(),
            packetsReceived: inboundRtp?.packetsReceived?.toString() || '0',
            packetsSent: outboundRtp?.packetsSent?.toString() || '0',
            peerIceConnectionState: this.pc.iceConnectionState,
            peerIceGatheringState: this.pc.iceGatheringState,
            peerLocalAddress: localCandidate
                ? `${localCandidate.address}:${localCandidate.port}`
                : '',
            peerLocalCandidateType: localCandidate?.candidateType || '',
            peerRemoteAddress: remoteCandidate
                ? `${remoteCandidate.address}:${remoteCandidate.port}`
                : '',
            peerRemoteCandidateType: remoteCandidate?.candidateType || '',
            peerSignalingState: this.pc.signalingState,
            peerTransportType: localCandidate?.protocol || 'udp',
            connectionTime: this.connectionTime,
            MOS: MOS
        }
    }

    /**
     * Считаем MOS
     * @param {Number} delayMs - задержка в мс
     * @param {Number} jitterBufferMs - джитер буфер в мс
     * @param {Number} packetsLost - потерянные пакеты
     * @returns {Number} MOS score (1-5)
     * @private
     */
    calculateMOS(delayMs, jitterBufferMs, packetsLost) {
        const effectiveLatency = delayMs + jitterBufferMs * 2 + 10
        let R

        if (effectiveLatency < 160) {
            R = 93.2 - effectiveLatency / 40
        } else {
            R = 93.2 - (effectiveLatency - 120) / 10
        }

        R = R - packetsLost * 2.5
        const MOS = R > 0 ? 1 + 0.035 * R + 0.000007 * R * (R - 60) * (100 - R) : 0

        return Math.round(MOS * 100) / 100 // Round to 2 decimal places
    }

    /**
     * Получить текущию статистику
     * @returns {Object|null} текущию статистика
     */
    getCallStats() {
        return this.stats
    }

    /**
     * Получаем состояние iceConnection
     * @returns {string} state
     */
    getIceState() {
        return this.pc?.iceConnectionState || 'closed'
    }

    /**
     * Создаем офер
     * @param {Function} callback - функция для возврата SDP
     */
    getOffer(callback) {
        logger.info('Creating SDP offer')

        this.pc.createOffer()
            .then(sessionDescription => {
                this.setLocalDescription(sessionDescription)
                callback(sessionDescription)
            })
            .catch(e => {
                logger.error(e, 'Failed to create offer')
            })
    }

    /**
     * Создаем ансфер на офер
     * @param {RTCSessionDescription} offer - офер
     * @param {Function} callback - функция для возврата SDP
     */
    getAnswer(offer, callback) {
        logger.info('Creating SDP answer')

        this.setRemoteDescription(new RTCSessionDescription(offer))

        this.pc.createAnswer()
            .then(sessionDescription => {
                this.setLocalDescription(sessionDescription)
                callback(sessionDescription)
            })
            .catch(e => {
                logger.error(e, 'Failed to create answer')
            })
    }

    /**
     * Устанавлием внешний ансфер
     * @param {RTCSessionDescription} answer - внешний ансфер
     */
    setAnswer(answer) {
        logger.info('Setting remote answer')
        this.setRemoteDescription(new RTCSessionDescription(answer))
    }

    /**
     * Добавляем кандидаты в PeerConnection
     * @param {RTCIceCandidate} candidate - кандидаты
     */
    addCandidate(candidate) {
        logger.info('Adding ICE candidate')

        try {
            const iceCandidate = new RTCIceCandidate(candidate)
            this.pc.addIceCandidate(iceCandidate)
        } catch (e) {
            logger.error(e, 'Failed to add ICE candidate')
        }
    }

    /**
     * Заменяем трек на новый
     * @param {MediaStreamTrack} oldTrack - старый трек
     * @param {MediaStreamTrack} newTrack - новый трек
     * @param {MediaStream} stream - стрим
     */
    async replaceTrack(oldTrack, newTrack, stream) {
        logger.info(`Replacing ${oldTrack.kind} track`)

        try {
            const sender = this.pc.getSenders().find(s => {
                return s?.track?.kind === oldTrack.kind
            })

            if (sender) {
                await sender.replaceTrack(newTrack)
                logger.info('Track replacement successful')
            } else {
                logger.error(new Error('Sender not found'), 'Cannot replace track - sender not found')
            }
        } catch (e) {
            logger.error(e, 'Failed to replace track')
        }
    }

    /**
     * Схлопываем PeerConnection и чистим свойства
     */
    close() {
        logger.info('Closing peer connection')

        if (this.statsInterval) {
            clearInterval(this.statsInterval)
            this.statsInterval = null
        }

        if (this.pc) {
            this.pc.close()
            this.pc = null
            this.onRemoteStream({ stream: null, connectingState: false })
        }

        this.noStats = true
    }

    /**
     * Устанавливаем описание локальной сессии
     * @param {RTCSessionDescription} sessionDescription - описание сессии
     * @private
     */
    setLocalDescription(sessionDescription) {
        logger.info('Setting local description')

        this.pc.setLocalDescription(sessionDescription)
            .catch(e => {
                logger.error(e, 'Failed to set local description')
            })
    }

    /**
     * Устанавливаем описание внешней сессии
     * @param {RTCSessionDescription} sessionDescription - описание сессии
     * @private
     */
    setRemoteDescription(sessionDescription) {
        logger.info('Setting remote description')

        this.pc.setRemoteDescription(sessionDescription)
            .catch(e => {
                logger.error(e, 'Failed to set remote description')
            })
    }
}